import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { ContributionApi, type CurrentContributionResponse } from '../services';
import { useAuth } from './AuthContext';

interface ContributionContextValue {
  currentContribution: CurrentContributionResponse | null;
  loading: boolean;
  error: string | null;
  refreshContribution: () => Promise<void>;
  initiateMpesaPayment: (amount?: number) => Promise<{ success: boolean; message: string; checkout_request_id?: string }>;
  processing: boolean;
}

const ContributionContext = createContext<ContributionContextValue | undefined>(undefined);

export const ContributionProvider = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [currentContribution, setCurrentContribution] = useState<CurrentContributionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);

  const refreshContribution = useCallback(async () => {
    if (!isAuthenticated) {
      setCurrentContribution(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await ContributionApi.getCurrentContribution();
      setCurrentContribution(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load contribution data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const initiateMpesaPayment = useCallback(
    async (amount?: number): Promise<{ success: boolean; message: string; checkout_request_id?: string }> => {
      setProcessing(true);
      setError(null);
      try {
        console.log('[ContributionContext] Initiating M-Pesa payment, amount:', amount);
        const result = await ContributionApi.initiateMpesaPayment(amount);
        console.log('[ContributionContext] M-Pesa payment initiated successfully:', result);
        return {
          success: true,
          message: result.message,
          checkout_request_id: result.checkout_request_id,
        };
      } catch (err) {
        console.error('[ContributionContext] M-Pesa payment initiation failed:', err);
        const message = err instanceof Error ? err.message : 'Failed to initiate M-Pesa payment';
        setError(message);
        return {
          success: false,
          message,
        };
      } finally {
        setProcessing(false);
      }
    },
    [refreshContribution]
  );

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isAuthenticated) {
      void refreshContribution();
    } else {
      setCurrentContribution(null);
      setError(null);
    }
  }, [authLoading, isAuthenticated, refreshContribution]);

  const value = useMemo<ContributionContextValue>(
    () => ({
      currentContribution,
      loading,
      error,
      refreshContribution,
      initiateMpesaPayment,
      processing,
    }),
    [currentContribution, loading, error, refreshContribution, initiateMpesaPayment, processing]
  );

  return <ContributionContext.Provider value={value}>{children}</ContributionContext.Provider>;
};

export const useContribution = () => {
  const context = useContext(ContributionContext);

  if (!context) {
    throw new Error('useContribution must be used within a ContributionProvider');
  }

  return context;
};

